// src/oura/components/ContradictionEvidence.tsx
//
// What an assistant said, against what the merchant's own document says
// (ourteam-frontend#3760, backend#4837, K-2).
//
//   ChatGPT told customers: "a KL clinic offering facials and massage
//   services, open until 6pm."
//   Their price list says: open until 9pm on weekdays, and they do not offer
//   massage.
//
// BOTH QUOTES, VERBATIM, SIDE BY SIDE. This is the kind of finding a merchant
// can act on AND verify for themselves, which is why a summary of the two is
// not good enough: the whole value is that they can read the assistant's
// sentence and their own sentence and see the clash.
//
// A contradiction is a finding about WHAT AN ASSISTANT SAID. It is not a
// measurement of the business and never moves a score, so this block must not
// read as a fault in the merchant's site.

import { AlertTriangleIcon, BotIcon, FileTextIcon } from "lucide-react";
import { SourceChip } from "../primitives/SourceChip";
import { cn } from "../lib/cn";
import { shortDate } from "../lib/oura";
import type { Citation } from "@service/aeo/types";

export interface ContradictionEvidence {
    question: string;
    /** What the assistant said, verbatim. */
    assistantQuote: string;
    /** What their document says, verbatim — the citation's own quote. */
    documentQuote: string;
    engine: string;
    /** ISO — when the assistant said it. */
    answeredAt: string | null;
    assessmentId: string | null;
    /** The cited document is over a year old. A caution, not a reason to hide. */
    stale: boolean;
    citation: Citation;
}

/** Engines are named as their makers write them, not as the API keys spell them. */
const ENGINE_LABEL: Record<string, string> = {
    openai: "ChatGPT",
    chatgpt: "ChatGPT",
    gemini: "Gemini",
    claude: "Claude",
    perplexity: "Perplexity",
};
const engineLabel = (e: string) => ENGINE_LABEL[(e || "").toLowerCase()] ?? (e || "An assistant");

/**
 * The evidence on a task, or null when this is not a contradiction.
 *
 * Narrowed here rather than cast at the callsite. `evidence` is free-shaped —
 * every rule decides its own — so a task carrying a DIFFERENT rule's evidence
 * must render as an ordinary task rather than as a half-filled contradiction
 * card. The two quotes and the citation's quote are what make the card worth
 * anything, so all three are required; everything else degrades.
 */
export function contradictionEvidence(
    task: { templateKey?: string | null; evidence?: Record<string, unknown> | null },
): ContradictionEvidence | null {
    if (task.templateKey !== "contradiction") return null;
    const e = task.evidence;
    if (!e || typeof e !== "object") return null;
    const citation = e.citation as Citation | undefined;
    const assistantQuote = typeof e.assistantQuote === "string" ? e.assistantQuote.trim() : "";
    const documentQuote = typeof e.documentQuote === "string" ? e.documentQuote.trim() : "";
    if (!assistantQuote || !documentQuote) return null;
    if (!citation || typeof citation.source_title !== "string" || typeof citation.quote !== "string") return null;
    return {
        question: typeof e.question === "string" ? e.question : "",
        assistantQuote,
        documentQuote,
        engine: typeof e.engine === "string" ? e.engine : "",
        answeredAt: typeof e.answeredAt === "string" ? e.answeredAt : null,
        assessmentId: typeof e.assessmentId === "string" ? e.assessmentId : null,
        stale: e.stale === true,
        citation,
    };
}

export function ContradictionEvidenceBlock({
    evidence,
    className,
}: {
    evidence: ContradictionEvidence;
    className?: string;
}) {
    const who = engineLabel(evidence.engine);
    return (
        <section className={cn("space-y-3", className)}>
            {evidence.question && (
                <p className="text-sm text-gray-500">
                    Someone asked: <span className="text-gray-900">{evidence.question}</span>
                </p>
            )}

            {/* Side by side on a desk, stacked on a phone — a merchant reads this
                between customers, and two half-width columns of quotes is not
                readable at that size. */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Quote
                    icon={BotIcon}
                    tone="assistant"
                    heading={who}
                    sub={evidence.answeredAt ? `said on ${shortDate(evidence.answeredAt.slice(0, 10))}` : "said"}
                    quote={evidence.assistantQuote}
                />
                <Quote
                    icon={FileTextIcon}
                    tone="document"
                    heading="Your own document says"
                    sub={evidence.citation.content_as_of
                        ? `true as of ${shortDate(evidence.citation.content_as_of)}`
                        : "no date on the content"}
                    quote={evidence.documentQuote}
                    footer={<SourceChip citations={[evidence.citation]} />}
                />
            </div>

            {evidence.stale && (
                <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    {/* Flagged, never suppressed: an old price list may still be
                        right, and hiding the finding decides that for the person
                        whose job it is. */}
                    The document this rests on is over a year old. Check it is still current before acting.
                </p>
            )}
        </section>
    );
}

function Quote({
    icon: Icon,
    tone,
    heading,
    sub,
    quote,
    footer,
}: {
    icon: typeof BotIcon;
    tone: "assistant" | "document";
    heading: string;
    sub: string;
    quote: string;
    footer?: React.ReactNode;
}) {
    return (
        <div className={cn(
            "rounded-lg border p-3",
            // The assistant's side is the one that is wrong, but it is not the
            // merchant's fault — grey, not red. Red here would read as a fault
            // in their site, which is exactly what this finding is not.
            tone === "assistant" ? "border-gray-200 bg-gray-50" : "border-blue-200 bg-blue-50/40",
        )}>
            <p className={cn(
                "flex items-center gap-1.5 text-xs font-semibold",
                tone === "assistant" ? "text-gray-600" : "text-blue-800",
            )}>
                <Icon className="h-3.5 w-3.5" />
                {heading}
            </p>
            <p className="mt-0.5 text-xs text-gray-400">{sub}</p>
            {/* Verbatim, and quoted as speech, so nobody reads it as our wording. */}
            <blockquote className="mt-2 border-l-2 border-gray-200 pl-2.5 text-sm leading-relaxed text-gray-800">
                “{quote}”
            </blockquote>
            {footer && <div className="mt-2">{footer}</div>}
        </div>
    );
}
